/**
 * BusinessDashboard.jsx
 *
 * A stripped-down, business-focused dashboard for the Business view.
 * Only visible to designated users (e.g. darren@operatr.com) — the route
 * itself is gated in Sidebar via PAGE_SECTIONS / allowedPaths, plus a
 * per-user check below.
 *
 * Intentionally does NOT include: profile card, clock/weather, app
 * shortcuts, brightness/volume sliders, Spotify player, AI Assistant.
 *
 * Surfaces: Today's Schedule (Calendar), Open Tasks, Upcoming Meetings
 * (CRM + Calendar-tagged).
 */
import React, { useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { CalendarDays, CheckSquare, Users, ArrowRight, Clock, MapPin, Video } from 'lucide-react';
import { useAuth, useUserId } from '../context/UserAuthContext';

const API_URL = typeof window !== 'undefined' ? window.location.origin : '';

// Restrict access at the page-level too — defense in depth.
const ALLOWED_EMAILS = new Set(['darren@operatr.com']);

const formatTime = (iso, fallback = '') => {
  if (!iso) return fallback;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return fallback;
  let h = d.getHours();
  const m = String(d.getMinutes()).padStart(2, '0');
  const ampm = h >= 12 ? 'PM' : 'AM';
  h = h % 12 || 12;
  return `${h}:${m} ${ampm}`;
};

const sameDay = (a, b) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

const BusinessDashboard = () => {
  const { user } = useAuth();
  const userId = useUserId();
  const userEmail = (user?.email || '').toLowerCase();

  const [todayEvents, setTodayEvents] = useState([]);
  const [openTasks, setOpenTasks] = useState([]);
  const [upcomingMeetings, setUpcomingMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [completingIds, setCompletingIds] = useState(new Set());

  // Gate: only allowed users see content (route is also filtered in sidebar)
  const hasAccess = ALLOWED_EMAILS.has(userEmail);

  useEffect(() => {
    if (!userId || !hasAccess) {
      setLoading(false);
      return;
    }
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      try {
        const now = new Date();
        const yr = now.getFullYear();
        const mo = now.getMonth() + 1;

        const [calRes, tasksRes, calMeetingsRes, crmMeetingsRes] = await Promise.all([
          fetch(`${API_URL}/api/outlook-calendar/desktop-events-by-month/${yr}/${mo}?user_id=${encodeURIComponent(userId)}`),
          fetch(`${API_URL}/api/tasks?user_id=${encodeURIComponent(userId)}&status=pending`),
          fetch(`${API_URL}/api/outlook-calendar/events-by-category?user_id=${encodeURIComponent(userId)}&category=meeting&days_back=0&days_forward=14`),
          fetch(`${API_URL}/api/meetings/?user_id=${encodeURIComponent(userId)}`),
        ]);

        // ---- Today's Schedule
        if (calRes.ok) {
          const data = await calRes.json();
          const events = [];
          Object.entries(data.events_by_day || {}).forEach(([day, dayEvents]) => {
            (dayEvents || []).forEach(e => {
              const d = e.start ? new Date(e.start) : null;
              if (d && !isNaN(d.getTime()) && sameDay(d, now)) {
                events.push({
                  id: e.id,
                  title: e.title || e.subject || 'Event',
                  time: formatTime(e.start),
                  end: formatTime(e.end),
                  start_ts: d.getTime(),
                  location: e.location,
                  online_meeting_url: e.online_meeting_url,
                  category: e.category,
                });
              }
            });
          });
          events.sort((a, b) => a.start_ts - b.start_ts);
          if (!cancelled) setTodayEvents(events);
        }

        // ---- Open Tasks
        if (tasksRes.ok) {
          const tasks = await tasksRes.json();
          if (!cancelled) setOpenTasks(Array.isArray(tasks) ? tasks.slice(0, 8) : []);
        }

        // ---- Upcoming Meetings (Calendar + CRM merged)
        const merged = [];
        if (calMeetingsRes.ok) {
          const j = await calMeetingsRes.json();
          (j.events || []).forEach(e => {
            const d = e.start ? new Date(e.start) : null;
            if (d && !isNaN(d.getTime()) && d.getTime() >= now.getTime() - 60 * 60 * 1000) {
              merged.push({
                id: `cal:${e.id}`,
                title: e.title || 'Calendar Meeting',
                when: d,
                location: e.location,
                online_meeting_url: e.online_meeting_url,
                source: 'calendar',
                pipeline_stage: null,
              });
            }
          });
        }
        let crmList = [];
        if (crmMeetingsRes.ok) {
          const raw = await crmMeetingsRes.json();
          crmList = Array.isArray(raw) ? raw : [];
          crmList.forEach(m => {
            const dt = m.date ? new Date(`${m.date}T${(m.time || '09:00')}:00`) : null;
            if (dt && !isNaN(dt.getTime()) && dt.getTime() >= now.getTime() - 60 * 60 * 1000) {
              merged.push({
                id: m._id || m.id,
                title: m.title || m.organization || 'Meeting',
                when: dt,
                location: m.location,
                online_meeting_url: null,
                source: 'crm',
                pipeline_stage: m.pipeline_stage || null,
              });
            }
          });
        }
        merged.sort((a, b) => a.when.getTime() - b.when.getTime());
        if (!cancelled) {
          setUpcomingMeetings(merged.slice(0, 8));
        }
      } catch (err) {
        console.error('BusinessDashboard load error:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [userId, hasAccess]);

  // Mark a task as completed (PATCH + optimistic remove from list)
  const handleCompleteTask = async (task) => {
    const taskId = task.id || task._id;
    if (!taskId || !userId) return;
    setCompletingIds(prev => {
      const next = new Set(prev);
      next.add(taskId);
      return next;
    });
    try {
      const res = await fetch(
        `${API_URL}/api/tasks/${encodeURIComponent(taskId)}?user_id=${encodeURIComponent(userId)}`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ status: 'completed', completed: true }),
        }
      );
      if (res.ok) {
        setOpenTasks(prev => prev.filter(t => (t.id || t._id) !== taskId));
      } else {
        console.error('Failed to complete task:', res.status);
      }
    } catch (err) {
      console.error('Complete task error:', err);
    } finally {
      setCompletingIds(prev => {
        const next = new Set(prev);
        next.delete(taskId);
        return next;
      });
    }
  };

  // ----- Access gate (defense in depth on top of sidebar filter)
  if (userEmail && !hasAccess) {
    return <Navigate to="/" replace />;
  }
  if (!userEmail) {
    return (
      <div className="min-h-screen flex items-center justify-center text-gray-400 text-sm">
        Signing you in…
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white px-4 md:px-8 py-6 md:py-10" data-testid="business-dashboard">
      {/* Header */}
      <div className="max-w-7xl mx-auto mb-8">
        <div className="flex items-center justify-between mb-2">
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight">Business Dashboard</h1>
          <span className="text-[11px] uppercase tracking-widest text-gray-500">Business View</span>
        </div>
        <p className="text-sm text-gray-400">Today's schedule, open tasks & upcoming meetings — no clutter.</p>
      </div>

      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-4 md:gap-6">
        {/* Today's Schedule */}
        <section className="lg:col-span-7 bg-black border border-gray-400 rounded-xl p-5 hover:border-gray-300 hover:shadow-[0_0_20px_rgba(156,163,175,0.5)] transition-all duration-300" data-testid="today-schedule">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-base font-semibold flex items-center gap-2">
              <CalendarDays size={18} className="text-sky-400" />
              Today's Schedule
            </h2>
            <Link
              to="/calendar"
              className="text-xs text-sky-400 hover:text-sky-300 flex items-center gap-1"
              data-testid="schedule-open-calendar-link"
            >
              Open Calendar <ArrowRight size={12} />
            </Link>
          </div>

          {loading ? (
            <div className="text-sm text-gray-500">Loading…</div>
          ) : todayEvents.length === 0 ? (
            <div className="text-sm text-gray-500 py-6 text-center">Nothing on the calendar today.</div>
          ) : (
            <ul className="space-y-2">
              {todayEvents.map(ev => (
                <li
                  key={ev.id}
                  className="flex items-start gap-3 bg-black/60 border border-gray-700 rounded-lg p-3 hover:border-gray-400 hover:shadow-[0_0_12px_rgba(156,163,175,0.3)] transition-all"
                  data-testid={`today-event-${ev.id}`}
                >
                  <div className="flex-shrink-0 w-20 text-xs text-sky-300 font-medium pt-0.5">
                    <div>{ev.time}</div>
                    {ev.end && <div className="text-gray-500 text-[10px]">→ {ev.end}</div>}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-white truncate">{ev.title}</div>
                    <div className="flex items-center gap-3 mt-1 text-[11px] text-gray-500">
                      {ev.location && (
                        <span className="flex items-center gap-1 truncate"><MapPin size={11} />{ev.location}</span>
                      )}
                      {ev.online_meeting_url && (
                        <a
                          href={ev.online_meeting_url}
                          target="_blank"
                          rel="noreferrer"
                          className="flex items-center gap-1 text-emerald-400 hover:text-emerald-300"
                          data-testid={`event-meeting-link-${ev.id}`}
                        >
                          <Video size={11} /> Join
                        </a>
                      )}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* Open Tasks */}
        <section className="lg:col-span-5 bg-black border border-gray-400 rounded-xl p-5 hover:border-gray-300 hover:shadow-[0_0_20px_rgba(156,163,175,0.5)] transition-all duration-300" data-testid="open-tasks">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-base font-semibold flex items-center gap-2">
              <CheckSquare size={18} className="text-amber-400" />
              Open Tasks
            </h2>
            <Link
              to="/tasks"
              className="text-xs text-amber-400 hover:text-amber-300 flex items-center gap-1"
              data-testid="tasks-open-tasks-link"
            >
              Open Tasks <ArrowRight size={12} />
            </Link>
          </div>

          {loading ? (
            <div className="text-sm text-gray-500">Loading…</div>
          ) : openTasks.length === 0 ? (
            <div className="text-sm text-gray-500 py-6 text-center">No open tasks. Inbox zero.</div>
          ) : (
            <ul className="space-y-2">
              {openTasks.map(t => {
                const taskId = t.id || t._id;
                const isCompleting = completingIds.has(taskId);
                return (
                  <li
                    key={taskId}
                    className="flex items-center gap-3 bg-black/60 border border-gray-700 rounded-lg p-3 hover:border-gray-400 hover:shadow-[0_0_12px_rgba(156,163,175,0.3)] transition-all"
                    data-testid={`task-${taskId}`}
                  >
                    <button
                      type="button"
                      onClick={() => handleCompleteTask(t)}
                      disabled={isCompleting}
                      title="Mark task complete"
                      data-testid={`task-checkbox-${taskId}`}
                      className={`flex-shrink-0 w-5 h-5 rounded border-2 transition-all flex items-center justify-center ${
                        isCompleting
                          ? 'border-amber-400 bg-amber-400/20 cursor-wait'
                          : 'border-gray-500 hover:border-amber-400 hover:bg-amber-400/10 cursor-pointer'
                      }`}
                    >
                      {isCompleting && (
                        <span className="block w-2.5 h-2.5 rounded-sm bg-amber-400 animate-pulse" />
                      )}
                    </button>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-white truncate">{t.title || t.text || '(untitled)'}</div>
                      {(t.category || t.priority) && (
                        <div className="text-[11px] text-gray-500 mt-0.5">
                          {t.category ? <span className="mr-2">{t.category}</span> : null}
                          {t.priority ? <span className="text-amber-400/80">[{t.priority}]</span> : null}
                        </div>
                      )}
                    </div>
                    {t.due_date && (
                      <div className="text-[11px] text-gray-500 flex items-center gap-1 flex-shrink-0">
                        <Clock size={11} /> {new Date(t.due_date).toLocaleDateString()}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        {/* Upcoming Meetings */}
        <section className="lg:col-span-12 bg-black border border-gray-400 rounded-xl p-5 hover:border-gray-300 hover:shadow-[0_0_20px_rgba(156,163,175,0.5)] transition-all duration-300" data-testid="upcoming-meetings">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-base font-semibold flex items-center gap-2">
              <Users size={18} className="text-purple-400" />
              Upcoming Meetings
            </h2>
            <Link
              to="/projects-re"
              className="text-xs text-purple-400 hover:text-purple-300 flex items-center gap-1"
              data-testid="meetings-open-link"
            >
              Open Meetings <ArrowRight size={12} />
            </Link>
          </div>

          {loading ? (
            <div className="text-sm text-gray-500">Loading…</div>
          ) : upcomingMeetings.length === 0 ? (
            <div className="text-sm text-gray-500 py-6 text-center">No upcoming meetings in the next 2 weeks.</div>
          ) : (
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {upcomingMeetings.map(m => (
                <li
                  key={m.id}
                  className="bg-black/60 border border-gray-700 rounded-lg p-3 hover:border-gray-400 hover:shadow-[0_0_12px_rgba(156,163,175,0.3)] transition-all"
                  data-testid={`upcoming-meeting-${m.id}`}
                >
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <div className="text-sm font-medium text-white truncate flex-1">{m.title}</div>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full flex-shrink-0 ${
                      m.source === 'calendar'
                        ? 'bg-sky-500/15 text-sky-300 border border-sky-500/30'
                        : 'bg-purple-500/15 text-purple-300 border border-purple-500/30'
                    }`}>
                      {m.source === 'calendar' ? 'Calendar' : 'CRM'}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 text-[11px] text-gray-500">
                    <span className="flex items-center gap-1">
                      <Clock size={11} />
                      {m.when.toLocaleDateString()} · {formatTime(m.when.toISOString())}
                    </span>
                    {m.location && (
                      <span className="flex items-center gap-1 truncate"><MapPin size={11} />{m.location}</span>
                    )}
                    {m.online_meeting_url && (
                      <a
                        href={m.online_meeting_url}
                        target="_blank"
                        rel="noreferrer"
                        className="text-emerald-400 hover:text-emerald-300 flex items-center gap-1"
                        data-testid={`meeting-join-${m.id}`}
                      >
                        <Video size={11} /> Join
                      </a>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
};

export default BusinessDashboard;
